import { useState } from "react";
import { Drawer, MenuItem, Select } from "@mui/material";
import { IconClose, IconChevronDown, IconSort, IconFilter } from "./icons";
import styles from "./SortFilterBar.module.css";

// Mobile-first sort + filter control.
//
// Desktop (≥ 900px): sort and page-size select sit inline next to the
// product count (existing toolbar look).
//
// Mobile (< 900px): everything collapses into a single "SORT & FILTER"
// button, opened the way the screenshot does it — sort surface up first,
// then a drawer for filters. Each `filtersNode` consumer (ItemListPage,
// future order/inventory views) passes the body that goes inside.

export default function SortFilterBar({
  count,
  pageSize,
  onPageSize,
  pageSizes = [12, 24, 48, 96],
  sort,
  onSort,
  sortOptions = [],
  filtersNode,
  onClearFilters,
  activeFilterCount = 0,
}) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("sort"); // 'sort' | 'filter'

  const sortLabel =
    sortOptions.find((o) => o.value === sort)?.label || "Sort";

  function openSort() { setView("sort"); setOpen(true); }
  function openFilter() { setView("filter"); setOpen(true); }

  return (
    <>
      <div className={styles.bar}>
        <div className={styles.count}>{count} {count === 1 ? "Product" : "Products"}</div>

        {/* Desktop inline controls */}
        <div className={styles.desktopControls}>
          <Select
            size="small"
            value={pageSize}
            onChange={(e) => onPageSize?.(Number(e.target.value))}
            variant="standard"
            disableUnderline
            className={styles.select}
          >
            {pageSizes.map((n) => <MenuItem key={n} value={n}>{n} per page</MenuItem>)}
          </Select>
          <Select
            size="small"
            value={sort}
            onChange={(e) => onSort?.(e.target.value)}
            variant="standard"
            disableUnderline
            className={styles.select}
          >
            {sortOptions.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </Select>
        </div>

        {/* Mobile single button per the screenshot */}
        <button
          type="button"
          className={styles.mobileBtn}
          onClick={openSort}
          aria-haspopup="dialog"
        >
          SORT & FILTER
          {activeFilterCount > 0 && <span className={styles.badge}>{activeFilterCount}</span>}
          <IconChevronDown size={16} />
        </button>
      </div>

      <Drawer
        anchor="bottom"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{ className: styles.sheet }}
      >
        <div className={styles.sheetHead}>
          <div className={styles.tabs} role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={view === "sort"}
              className={[styles.tab, view === "sort" ? styles.tabActive : ""].join(" ")}
              onClick={() => setView("sort")}
            >
              <IconSort size={16} /> Sort
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={view === "filter"}
              className={[styles.tab, view === "filter" ? styles.tabActive : ""].join(" ")}
              onClick={() => setView("filter")}
            >
              <IconFilter size={16} /> Filter
              {activeFilterCount > 0 && <span className={styles.badge}>{activeFilterCount}</span>}
            </button>
          </div>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={() => setOpen(false)}
            aria-label="Close"
          >
            <IconClose />
          </button>
        </div>

        {view === "sort" ? (
          <ul className={styles.sortList}>
            {sortOptions.map((o) => (
              <li key={o.value}>
                <button
                  type="button"
                  className={[styles.sortRow, sort === o.value ? styles.sortRowActive : ""].join(" ")}
                  onClick={() => { onSort?.(o.value); setOpen(false); }}
                >
                  {o.label}
                  {sort === o.value && <span className={styles.dot} aria-hidden />}
                </button>
              </li>
            ))}
            <li className={styles.divider} />
            <li>
              <div className={styles.perPageRow}>
                <span>Per page</span>
                <Select
                  size="small"
                  value={pageSize}
                  onChange={(e) => onPageSize?.(Number(e.target.value))}
                  variant="standard"
                  disableUnderline
                >
                  {pageSizes.map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                </Select>
              </div>
            </li>
          </ul>
        ) : (
          <div className={styles.filterBody}>
            {filtersNode}
          </div>
        )}

        <div className={styles.sheetFoot}>
          {view === "filter" && onClearFilters && (
            <button type="button" className={styles.clearBtn} onClick={onClearFilters}>
              Clear
            </button>
          )}
          <button
            type="button"
            className={styles.applyBtn}
            onClick={() => setOpen(false)}
          >
            View {count} result{count === 1 ? "" : "s"}
          </button>
        </div>
      </Drawer>
    </>
  );
}
